import React, { useMemo } from 'react';

import { ShopOutlined, SwapOutlined } from '@ant-design/icons';
import { List, Progress, Tag, Typography } from 'antd';

import type { ProductsPayload } from '../hooks/useDashboardStats';

type Row = { productId: string; productName: string; count: number };

type Props = {
  products?: ProductsPayload;
  loading?: boolean;
};

function ProductList({
  title,
  icon,
  iconClass,
  rows,
  maxCount,
  loading,
  emptyText,
  topBarColor,
}: {
  title: string;
  icon: React.ReactNode;
  iconClass: string;
  rows: Row[];
  maxCount: number;
  loading?: boolean;
  emptyText: string;
  topBarColor?: string;
}) {
  return (
    <section className="dashboard-top-products__col">
      <div className="dashboard-top-products__section-header">
        <span className={`dashboard-top-products__section-icon ${iconClass}`}>
          {icon}
        </span>
        <Typography.Title level={5} className="dashboard-top-products__title">
          {title}
        </Typography.Title>
      </div>
      <List<Row>
        size="small"
        loading={loading}
        dataSource={rows}
        locale={{ emptyText }}
        className="dashboard-top-products__list"
        renderItem={(item, index) => (
          <List.Item className="dashboard-top-products__list-item">
            <Typography.Text type="secondary" className="dashboard-top-products__rank">
              {index + 1}
            </Typography.Text>
            <div className="dashboard-top-products__list-body">
              <div className="dashboard-top-products__list-meta">
                <Tag className="dashboard-top-products__ref-tag">{item.productId}</Tag>
                <Typography.Text
                  ellipsis
                  className="dashboard-top-products__product-name"
                >
                  {item.productName}
                </Typography.Text>
              </div>
              <div className="dashboard-top-products__count-cell">
                <Progress
                  percent={maxCount > 0 ? Math.round((item.count / maxCount) * 100) : 0}
                  size="small"
                  showInfo={false}
                  strokeColor={index === 0 && topBarColor ? topBarColor : undefined}
                  className="dashboard-top-products__bar"
                />
                <Typography.Text strong className="dashboard-top-products__count-num">
                  {item.count}
                </Typography.Text>
              </div>
            </div>
          </List.Item>
        )}
      />
    </section>
  );
}

export const TopProductsTable = ({ products, loading }: Props) => {
  const ordered = useMemo(() => products?.topOrderedProducts ?? [], [products]);
  const returned = useMemo(() => products?.topReturnedProducts ?? [], [products]);

  const maxOrdered = useMemo(
    () => Math.max(...ordered.map((r) => r.count), 1),
    [ordered],
  );
  const maxReturned = useMemo(
    () => Math.max(...returned.map((r) => r.count), 1),
    [returned],
  );

  return (
    <div className="dashboard-top-products">
      <ProductList
        title="Most ordered"
        icon={<ShopOutlined />}
        iconClass="dashboard-top-products__section-icon--order"
        rows={ordered}
        maxCount={maxOrdered}
        loading={loading}
        emptyText="No orders in this period"
        topBarColor="#52c41a"
      />
      <ProductList
        title="Most returned"
        icon={<SwapOutlined />}
        iconClass="dashboard-top-products__section-icon--return"
        rows={returned}
        maxCount={maxReturned}
        loading={loading}
        emptyText="No returns in this period"
        topBarColor="#ff4d4f"
      />
    </div>
  );
};
